import React from "react";
import { UIButton } from "@src/components/UIButton";
import { theme } from "@src/theme";
import ResetSVG from "@assets/img/reset-icon.svg";
import ResetSVGDisabled from "@assets/img/reset-icon-gray.svg";
import DownloadButton from "@assets/img/download-circle-icon.svg";
import DownloadButtonGray from "@assets/img/download-gray.svg";
import CDButton from "@assets/img/cd-icon.svg";
import CDButtonGray from "@assets/img/play-playlist-gray.svg";
import {
  PlaylistBarContainer,
  PlaylistDetailsBarColContainer,
  PlaylistDetailsBarInfoText,
  PlaylistDetailsBarInfoValue,
  PlaylistDetailsBarPlaylistInfo,
} from "./PlaylistDetailsBar.styles";
import {
  IPlaylistRecord,
  PlaylistActionType,
  PlaylistStatus,
} from "@src/types/playlist";
import Spinner from "@src/components/Spinner";
import { useAppSelector } from "@src/redux/hooks";
import Tooltip from "@src/components/Tooltip";

type PlaylistDetailsTopBarProps = {
  playlistDetails: IPlaylistRecord | null;
  isLoadingPlaylistDetails?: boolean;
  isPlaylistDurationSufficient?: boolean;
  currentPlaylistAction?: PlaylistActionType;
  currentDuration?: string;
  trackCount?: number;
  isPlaylistTracksValid?: boolean;
  status?: PlaylistStatus;
  isDownloadingPlaylist?: boolean;
  onReset?: () => void;
  onCutMix?: () => void;
  onAutoMix?: () => void;
  onDownload?: () => void;
  onPlay?: () => void;
  onSubmit?: () => void;
  onClick?: () => void;
};
const PlaylistDetailsTopBar: React.FC<PlaylistDetailsTopBarProps> = ({
  playlistDetails,
  isLoadingPlaylistDetails,
  isPlaylistDurationSufficient,
  currentPlaylistAction,
  currentDuration,
  trackCount,
  isPlaylistTracksValid = true,
  status,
  onReset,
  onCutMix,
  onAutoMix,
  onDownload,
  onPlay,
  onSubmit,
  onClick,
  isDownloadingPlaylist,
}) => {
  const isPlaylistActionSelected =
    currentPlaylistAction !== "none" && currentPlaylistAction !== null;
  const disableReset =
    isLoadingPlaylistDetails ||
    !isPlaylistActionSelected ||
    !(status === "draft" || status === "ready");
  const disableCutMix =
    isLoadingPlaylistDetails ||
    status !== "ready" ||
    isPlaylistActionSelected ||
    !isPlaylistDurationSufficient;
  const disableAutoMix =
    isLoadingPlaylistDetails ||
    status !== "ready" ||
    isPlaylistActionSelected ||
    !isPlaylistDurationSufficient;
  const disableSubmit =
    status === "submitted" ||
    isLoadingPlaylistDetails ||
    status !== "ready" ||
    !playlistDetails?.playlistProcessedAudio?.url;

  const mixTooltip = !isPlaylistDurationSufficient
    ? "Playlist duration is insufficient to process"
    : undefined;

  const submitTooltip = !isPlaylistTracksValid
    ? "Some tracks are missing Title or Artist — fill in the info before submitting"
    : !playlistDetails?.playlistProcessedAudio?.url
      ? "Playlist must be processed before submitting"
      : undefined;

  const noAudioFile =
    isLoadingPlaylistDetails || !playlistDetails?.playlistProcessedAudio?.url;
  const { trackStatus, currentPlayingProcessedPlaylist } = useAppSelector(
    (state) => state.trackPlayer
  );
  const loadingPlaylistPreview =
    trackStatus === "loading" && currentPlayingProcessedPlaylist?.documentId;
  return (
    <PlaylistBarContainer onClick={onClick}>
      <PlaylistDetailsBarColContainer
        style={{
          marginLeft: theme.margins.xl,
        }}
      >
        <PlaylistDetailsBarPlaylistInfo>
          <PlaylistDetailsBarInfoText>
            Current duration:
          </PlaylistDetailsBarInfoText>
          <PlaylistDetailsBarInfoValue
            style={{
              color: isPlaylistDurationSufficient
                ? theme.colors.yellow
                : theme.colors.gray,
            }}
          >
            {currentDuration}
          </PlaylistDetailsBarInfoValue>
        </PlaylistDetailsBarPlaylistInfo>

        <PlaylistDetailsBarPlaylistInfo>
          <PlaylistDetailsBarInfoText>Track Count:</PlaylistDetailsBarInfoText>
          <PlaylistDetailsBarInfoValue>
            {trackCount}
          </PlaylistDetailsBarInfoValue>
        </PlaylistDetailsBarPlaylistInfo>

        <PlaylistDetailsBarPlaylistInfo>
          <PlaylistDetailsBarInfoText>Status</PlaylistDetailsBarInfoText>
          <PlaylistDetailsBarInfoValue
            style={{
              color:
                status === "submitted"
                  ? theme.colors.green
                  : status === "ready"
                    ? theme.colors.yellow
                    : theme.colors.gray,
            }}
          >
            {status}
          </PlaylistDetailsBarInfoValue>
        </PlaylistDetailsBarPlaylistInfo>
      </PlaylistDetailsBarColContainer>
      <PlaylistDetailsBarColContainer style={{ justifyContent: "center" }}>
        <UIButton
          disabled={disableReset}
          onClick={onReset}
          variant={disableReset ? "secondaryOutline" : "primaryOutline"}
          style={{ minWidth: 90, fontWeight: "bold" }}
        >
          <img
            src={disableReset ? ResetSVGDisabled : ResetSVG}
            style={{ marginRight: theme.margins.small }}
          />
          Reset
        </UIButton>
        <UIButton
          disabled={disableCutMix}
          onClick={onCutMix}
          tooltipContent={disableCutMix ? mixTooltip : undefined}
          variant={
            playlistDetails?.playlistAction === "cut"
              ? "primary"
              : disableCutMix
                ? "secondaryOutline"
                : "primaryOutline"
          }
          style={{ minWidth: 90, fontWeight: "bold" }}
        >
          Cut Mix
        </UIButton>
        <UIButton
          disabled={disableAutoMix}
          onClick={onAutoMix}
          tooltipContent={disableAutoMix ? mixTooltip : undefined}
          variant={
            playlistDetails?.playlistAction === "crossfade"
              ? "primary"
              : disableAutoMix
                ? "secondaryOutline"
                : "primaryOutline"
          }
          style={{ minWidth: 90, fontWeight: "bold" }}
        >
          Auto Mix
        </UIButton>
        {/*
         * Read-only DAW indicator, shown only for non-draft/non-ready
         * playlists (i.e. "submitted"/"aired"): reflects whether the playlist
         * was mixed in the DAW (playlistAction === "mixer").
         */}
        {status !== "draft" && status !== "ready" && (
          <UIButton
            disabled
            tooltipContent={
              playlistDetails?.playlistAction === "mixer"
                ? "Mixer was used"
                : "Mixer not used"
            }
            variant={
              playlistDetails?.playlistAction === "mixer"
                ? "primary"
                : "secondaryOutline"
            }
            style={{ minWidth: 90, fontWeight: "bold" }}
          >
            DAW
          </UIButton>
        )}
      </PlaylistDetailsBarColContainer>
      <PlaylistDetailsBarColContainer
        style={{ justifyContent: "flex-end", marginRight: theme.margins.xl }}
      >
        <UIButton
          tooltipContent={isDownloadingPlaylist ? "Downloading" : "Download"}
          disabled={noAudioFile || !onDownload}
          onClick={onDownload}
          variant="transparent"
        >
          {isDownloadingPlaylist ? (
            <Spinner small />
          ) : (
            <img
              src={noAudioFile ? DownloadButtonGray : DownloadButton}
              alt="Download"
            />
          )}
        </UIButton>
        <UIButton
          tooltipContent={"Preview mix"}
          disabled={!!(noAudioFile || loadingPlaylistPreview)}
          onClick={onPlay}
          variant="transparent"
        >
          <img
            src={
              noAudioFile || loadingPlaylistPreview ? CDButtonGray : CDButton
            }
            alt="Play"
          />
        </UIButton>
        {loadingPlaylistPreview && (
          <div>
            <Tooltip content="Loading Playlist Preview">
              <Spinner small />
            </Tooltip>
          </div>
        )}
        <UIButton
          disabled={disableSubmit}
          onClick={onSubmit}
          tooltipContent={submitTooltip}
          style={{ minWidth: 90, fontWeight: "bold" }}
          variant={disableSubmit ? "secondaryOutline" : "primaryOutline"}
        >
          Submit
        </UIButton>
      </PlaylistDetailsBarColContainer>
    </PlaylistBarContainer>
  );
};

export default PlaylistDetailsTopBar;
